import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResearchAgreementWithStudentName } from '../../Types/interfaces';
import { ResearchAgreementStatus } from '../../Types/interfaces';
import { GetResearchAgreementById } from '../../helper/apiHelper';
import { Tabs, Divider, Skeleton, Space, notification, Badge } from 'antd';
import { AgreementTabForm } from './AgreementTabForm';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { EMPTY_ARRAY } from '../../Constants/constants';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import noResearchAgreementImg from '../../Assets/Images/noResearchAgreement.png';
import { NoData } from '../NoData';

const ResearchAgreementComponent = (): React.ReactElement => {
  const { t } = useTranslation();
  const loggedInUser = useCurrentUser();
  const {
    data: agreements = EMPTY_ARRAY,
    isLoading,
    refetch: reFetchData,
  } = useQuery({
    queryKey: [QUERY_KEYS.RESEARCH_AGREEMENTS, loggedInUser.id],
    queryFn: async () => {
      return await GetResearchAgreementById(loggedInUser.id);
    },
    enabled: !!loggedInUser.id,
  });

  const rejectedAgreements = agreements.filter(
    (item) => item.status === ResearchAgreementStatus.Rejected
  );

  const rejectedResearchAgreementsCount = rejectedAgreements.length;

  useEffect(() => {
    if (rejectedResearchAgreementsCount > 0) {
      notification.error({
        key: 'rejected-agreement-notification',
        message: t('researchAgreementComp.notificationRejectTitle'),
        description: t('researchAgreementComp.notificationRejectDesc'),
        placement: 'bottomLeft',
        duration: 0,
      });
    }
  }, [rejectedResearchAgreementsCount, t]);

  const isMasterStudent = (): boolean => {
    return agreements.length > 0;
  };

  if (isLoading) {
    return (
      <div>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  return (
    <>
      {isMasterStudent() ? (
        <>
          <Divider plain style={{ fontSize: 20 }}>
            {t('researchAgreementComp.researchTopicAgreement')}
          </Divider>
          <Tabs
            tabPosition="top"
            centered
            tabBarGutter={16}
            size="large"
            type="card"
            items={agreements.map((item: ResearchAgreementWithStudentName) => {
              return {
                key: String(item.researchAgreementId),
                label: (
                  <Space size="small">
                    <span>{item.organizationName}</span>
                    {item.status === ResearchAgreementStatus.Rejected && (
                      <Badge
                        count={rejectedResearchAgreementsCount}
                        offset={[10, -20]}
                      />
                    )}
                  </Space>
                ),
                children: (
                  <AgreementTabForm
                    item={item}
                    updateAgreements={reFetchData}
                  />
                ),
              };
            })}
          />
        </>
      ) : (
        <NoData imgSource={noResearchAgreementImg} />
      )}
    </>
  );
};
export default ResearchAgreementComponent;
